import { useState, type FormEvent } from 'react';
import type { MeResponse } from '@whaletail/shared';
import { ApiError, changePassword, getMe } from '../api.ts';

interface Props {
  username: string;
  /** Called with a freshly re-fetched /api/me once the new password is set — passwordExpired will be false. */
  onChanged: (me: MeResponse) => void;
  onSignOut: () => void;
}

const PASSWORD_MIN = 8;

/**
 * Shown instead of the app (same idea as LoginGate — see App.tsx's gate)
 * whenever an admin has expired this account's password. The account is
 * already signed in — this only asks for the current password to prove it's
 * really them, same as the ordinary change-password form in Settings, not a
 * password reset. The server enforces this too (requireActiveUser in
 * routes-account.ts blocks every other route until it's cleared), so this
 * screen isn't just a client-side suggestion.
 */
export function PasswordExpiredGate({ username, onChanged, onSignOut }: Props) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (next.length < PASSWORD_MIN) return setError(`Use at least ${PASSWORD_MIN} characters for your new password.`);
    if (next !== confirm) return setError("Those passwords don't match.");

    setBusy(true);
    try {
      await changePassword(current, next);
      onChanged(await getMe());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gate">
      <form onSubmit={submit} className="gate-card">
        <picture className="brand gate-brand">
          <source srcSet="/whaletail-vertical-dark.png" media="(prefers-color-scheme: dark)" />
          <img src="/whaletail-vertical.png" alt="WhaleTail" width={1988} height={1760} />
        </picture>

        <p className="dialog-lede">
          An admin has expired the password for <strong>{username}</strong>. Set a new one to continue.
        </p>

        <label className="field">
          <span>Current password</span>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
            maxLength={200}
            autoFocus
          />
        </label>

        <label className="field">
          <span>New password</span>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            required
            maxLength={200}
          />
          <small>At least {PASSWORD_MIN} characters. A few random words works well.</small>
        </label>

        <label className="field">
          <span>Confirm new password</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
            maxLength={200}
          />
        </label>

        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="btn btn-primary btn-block gate-submit" disabled={busy}>
          {busy ? 'Please wait…' : 'Set new password'}
        </button>

        <p className="dialog-switch">
          Wrong account?{' '}
          <button type="button" className="link-btn" onClick={onSignOut}>
            Sign Out
          </button>
        </p>
      </form>
    </div>
  );
}
